import type { AppVersion } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import { listLatestProductionVersions } from '@wix/ambassador-devcenter-apps-v1-app-version/http';
import { HttpClient, IHttpClient } from '@wix/http-client';
import _ from 'lodash';
import { App } from '../../../../types/appManager';
import type { MarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import { queryMarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/http';
import { queryApp } from '@wix/ambassador-devcenter-apps-v1-app/http';
import { normalizeApps } from './getMarketListingApps.utils';

export const fetchLatestVersions = async (
  appIds: string[],
  httpClient: IHttpClient = new HttpClient(),
) => {
  const fetchLatestVersionsChunk = async (appIdsChunk: string[]) => {
    const response = await httpClient.request(
      listLatestProductionVersions({ appIds: appIdsChunk }),
    );

    return response?.data?.appVersions;
  };

  let appVersionList: AppVersion[] = [];
  const appIdsChunks = _.chunk<string>(appIds, 50);

  for (const appIdsChunk of appIdsChunks) {
    const appVersions = await fetchLatestVersionsChunk(appIdsChunk);

    if (!appVersions) {
      continue;
    }

    appVersionList = appVersionList.concat(appVersions);
  }

  return appVersionList;
};

export const fetchMarketListingApps = async (
  appIds: string[],
  locale: string,
  httpClient: IHttpClient = new HttpClient(),
) => {
  const fetchMarketListingAppsChunk = async (appIdsChunk: string[]) => {
    const { data } = await httpClient.request(
      queryMarketListing({
        query: {
          filter: {
            appId: appIdsChunk,
            languageCode: locale,
            status: 'PUBLISHED',
          },
        },
      }),
    );

    return data;
  };

  let marketListingApps: MarketListing[] = [];
  const appIdsChunks = _.chunk<string>(appIds, 50);

  for (const appIdsChunk of appIdsChunks) {
    const marketListingAppsData = await fetchMarketListingAppsChunk(
      appIdsChunk,
    );

    if (!marketListingAppsData) {
      continue;
    }

    marketListingApps = marketListingApps.concat(
      marketListingAppsData.marketListing!,
    );
  }

  return marketListingApps;
};

const fetchQueryApp = (
  appIds: string[],
  httpClient: IHttpClient = new HttpClient(),
) => {
  return httpClient.request(queryApp({ query: { filter: { id: appIds } } }));
};

export const getMarketListingApps = async (
  appIds: string[],
  locale: string,
  httpClient: IHttpClient = new HttpClient(),
) => {
  const [marketListings, queryAppRes, latestAppVersions] = await Promise.all([
    fetchMarketListingApps(appIds, locale, httpClient),
    fetchQueryApp(appIds, httpClient),
    fetchLatestVersions(appIds, httpClient),
  ]);

  const queryAppByAppId = _.keyBy(queryAppRes.data.apps, (item) => item.id!);

  const latestAppVersionsById = _.keyBy(latestAppVersions, (item) => item.id!);

  const formattedApps = marketListings?.map((marketListing: MarketListing) => ({
    id: marketListing.appId,
    name: marketListing.basicInfo?.name,
    version: String(latestAppVersionsById[marketListing.appId!].fullVersion),
    status: marketListing.status,
    market: {
      marketListing: {
        assetsMap: marketListing.assetsMap,
        basicInfo: marketListing.basicInfo,
      },
      slug: queryAppByAppId[marketListing.appId as string].slug,
    },
  }));

  return normalizeApps(formattedApps?.length ? (formattedApps as App[]) : []);
};
